import { comparisonColor, comparisonPositive } from '../../helpers/Custom';
import { Dashboard } from '../../models/ApiModels';
import { buildDashboardTargets, clampProgress, TargetUnit } from './dashboardTargetsUtils';

interface Props {
  dashboardData: Dashboard;
  compare: boolean;
}

const ksekNumberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

function DashboardTargets({ dashboardData, compare }: Props) {
  const rows = buildDashboardTargets(dashboardData);

  const formatValue = (value: number, unit?: TargetUnit) => {
    const rounded = Math.round(value);
    if (!unit) return `${rounded}`;
    if (unit === '%') return `${rounded}%`;
    return `${ksekNumberFormatter.format(rounded)} ${unit}`;
  };

  const formatDiffValue = (value: number, unit?: TargetUnit) => {
    const rounded = Math.round(value);
    if (unit === 'KSEK') return ksekNumberFormatter.format(rounded);
    return `${rounded}`;
  };

  return (
    <div className='mt-10'>
      <div className='prose-heading4 uppercase mb-5'>Targets 2026</div>
      <div className='bg-gray-3 p-8 flex flex-col gap-2'>
        {rows.map((row) => {
          const previous = row.current - row.diff;
          const currentProgress = clampProgress(row.target > 0 ? (row.current / row.target) * 100 : 0);
          const previousProgress = clampProgress(row.target > 0 ? (previous / row.target) * 100 : 0);
          const baseProgress = compare ? Math.min(currentProgress, previousProgress) : currentProgress;
          const deltaProgress = compare ? Math.abs(currentProgress - previousProgress) : 0;
          const deltaColor = row.diff > 0 ? '#BFE5A7' : '#FEB1B1';
          const hiddenOverTargetDelta =
            compare &&
            row.diff !== 0 &&
            currentProgress === 100 &&
            previousProgress === 100 &&
            (row.current > row.target || previous > row.target);
          const roundedDiff = Math.round(row.diff);
          const diffLabel = `${comparisonPositive(roundedDiff)}${formatDiffValue(roundedDiff, row.unit)}`;
          const diffSuffix = row.unit === 'KSEK' ? ` ${row.unit}` : row.unit === '%' ? row.unit : '';
          const diffMarkerTitle = `Diff ${diffLabel}${diffSuffix}`;

          return (
            <div
              key={row.label}
              className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4'
            >
              <div className='w-full text-black prose-paragraphBase sm:w-[100px] sm:flex-none'>{row.label}</div>
              <div className='relative w-full h-[25px] bg-gray-10 overflow-hidden sm:flex-1'>
                <div
                  className='absolute left-0 top-0 h-full'
                  style={{
                    width: `${baseProgress}%`,
                    backgroundColor: row.barColor,
                  }}
                ></div>
                {compare && row.diff !== 0 && deltaProgress > 0 && (
                  <div
                    className='absolute top-0 h-full'
                    style={{
                      left: `${baseProgress}%`,
                      width: `${deltaProgress}%`,
                      backgroundColor: deltaColor,
                    }}
                  ></div>
                )}
                {hiddenOverTargetDelta && (
                  <div
                    className='absolute top-0 right-0 h-full w-[3px]'
                    title={diffMarkerTitle}
                    style={{
                      backgroundColor: deltaColor,
                    }}
                  ></div>
                )}
              </div>
              <div className='w-full flex items-center gap-2 text-black sm:w-[220px] sm:flex-none'>
                <span>{formatValue(row.current, row.unit)}</span>
                {compare && (
                  <span className={comparisonColor(roundedDiff)}>
                    {diffLabel}
                  </span>
                )}
                <span className='prose-paragraphBase leading-none'>/</span>
                <span>{formatValue(row.target, row.unit)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DashboardTargets;
